<script lang="ts">
  import { defineComponent } from 'vue';

  import { Business } from '@/types/widgets/commercialTenantsWidget/commercialTenantsWidget';

  import { ALIGN_MODES } from '@/models/layoutDesigner';

  export default defineComponent({
    name: 'CommercialTenantsViewer',
  });
</script>

<script setup lang="ts">
  import { ref, watch, toRef, computed, toRefs, onMounted, onUnmounted } from 'vue';

  import Loader from '@/components/common/Loader.vue';

  import { apiGetDetailedCommercialAddress } from '@/api/tenants';
  import { CommercialTenant } from '@/types/api/tenants';

  import { divideArray } from '@/helpers';
  import { getRBGAColor, hex8ToRgba, isHex8 } from '@/helpers/utils';
  import { logger } from '@/utils/screenEventsLogger';

  const props = defineProps({
    widgetData: { type: Object, default: {} },
  });

  const timer = ref<ReturnType<typeof setInterval> | null>(null);
  const currentPage = ref<number>(1);
  const isLoading = ref(false);
  const errorLoading = ref(false);
  const fetchedTenants = ref<CommercialTenant[]>([]);
  const initialLoad = ref<boolean>(true);
  const skipInitialAnimation = ref<boolean>(true);

  const { tenants } = toRefs(props.widgetData);
  const slideInterval = toRef(props.widgetData.tenants, 'interval');
  const allBusinessesVisible = toRef(props.widgetData.tenants, 'allBusinessesVisible');
  const tenantsOnSlide = toRef(props.widgetData.tenants, 'tenantsOnSlide');

  watch([slideInterval, allBusinessesVisible, tenantsOnSlide], () => {
    resetSlide();
  });

  const containerStyle = computed(() => ({
    display: 'grid',
    gridAutoFlow: 'row',
    gridAutoRows: 'max-content',
    gridTemplateColumns: '1fr',
    paddingTop: `${tenants.value.topPadding || 20}px`,
  }));

  const wrapperStyle = computed(() => {
    return {
      backgroundColor: getRBGAColor(
        tenants.value.backgroundColor,
        Number(tenants.value.opacity) / 100,
      ),
    };
  });

  const businessesContainerStyle = computed(() => {
    const justifyContent = !tenants.value.groupNamesAndNumbers
      ? 'space-between'
      : tenants.value.textAlignment;

    return {
      flexDirection: !tenants.value.numbersOnTheLeft ? 'row' : 'row-reverse',
      justifyContent,
      paddingTop:
        !!tenants.value.verticalPadding || tenants.value.verticalPadding === 0
          ? `${tenants.value.verticalPadding}px`
          : '8px',
      paddingBottom:
        !!tenants.value.verticalPadding || tenants.value.verticalPadding === 0
          ? `${tenants.value.verticalPadding}px`
          : '8px',
    } as any;
  });

  const addressTitleStyle = computed(() => {
    const addressFontColor = tenants.value.addressFontColor || '#000000';
    const addressBackgroundColor = tenants.value.addressBackgroundColor || 'transparent';

    return {
      fontFamily: tenants.value.addressFont || 'Arial',
      fontSize: `${tenants.value.addressFontSize || 24}px`,
      lineHeight: `${(tenants.value.addressFontSize || 24) * 1.3}px`,
      color:
        addressFontColor && isHex8(addressFontColor)
          ? hex8ToRgba(addressFontColor)
          : addressFontColor,
      backgroundColor:
        addressBackgroundColor && isHex8(addressBackgroundColor)
          ? hex8ToRgba(addressBackgroundColor)
          : addressBackgroundColor,
      textAlign: tenants.value.addressAlignment || ALIGN_MODES.LEFT_ALIGN,
      paddingTop: `${
        tenants.value.addressVerticalPadding === undefined ||
        tenants.value.addressVerticalPadding === 0
          ? 24
          : tenants.value.addressVerticalPadding
      }px`,
      paddingBottom: `${
        tenants.value.addressVerticalPadding === undefined ||
        tenants.value.addressVerticalPadding === 0
          ? 24
          : tenants.value.addressVerticalPadding
      }px`,
      paddingLeft: `${
        tenants.value.addressHorizontalPadding === undefined ||
        tenants.value.addressHorizontalPadding === 0
          ? 20
          : tenants.value.addressHorizontalPadding
      }px`,
      paddingRight: `${
        tenants.value.addressHorizontalPadding === undefined ||
        tenants.value.addressHorizontalPadding === 0
          ? 20
          : tenants.value.addressHorizontalPadding
      }px`,
      display: tenants.value.showAddress ? 'block' : 'none',
    };
  });

  const tenantNameStyle = computed(() => {
    const fontColor = tenants.value.businessFontColor;

    return {
      fontFamily: tenants.value.businessFont,
      fontSize: `${tenants.value.businessFontSize}px`,
      lineHeight: `${tenants.value.businessFontSize * 1.3}px`,
      color: fontColor && isHex8(fontColor) ? hex8ToRgba(fontColor) : fontColor,
      textAlign: tenants.value.textAlignment || ALIGN_MODES.LEFT_ALIGN,
      width: !tenants.value.groupNamesAndNumbers ? '100%' : 'auto',
    };
  });

  const tenantNumberStyle = computed(() => {
    const fontColor = tenants.value.numberFontColor;

    return {
      fontFamily: tenants.value.numberFont,
      fontSize: `${tenants.value.numberFontSize}px`,
      lineHeight: `${tenants.value.numberFontSize * 1.3}px`,
      color: fontColor && isHex8(fontColor) ? hex8ToRgba(fontColor) : fontColor,
      textAlign: tenants.value.numberAlignment || ALIGN_MODES.LEFT_ALIGN,
      display: !tenants.value.hideNumbers ? 'block' : 'none',
    };
  });

  const paginationStyle = computed(() => {
    const fontColor = tenants.value.paginationFontColor;

    return {
      fontFamily: tenants.value.paginationFont,
      fontSize: `${tenants.value.paginationFontSize}px`,
      lineHeight: `${tenants.value.paginationFontSize * 1.2}px`,
      color: fontColor && isHex8(fontColor) ? hex8ToRgba(fontColor) : fontColor,
      justifyContent: tenants.value.paginationAlignment || ALIGN_MODES.CENTER_ALIGN,
      height: tenants.value.paginationInText
        ? `${tenants.value.paginationFontSize + 16}px`
        : 'auto',
    };
  });

  const lineSeparatorStyle = computed(() => {
    const separatorColor = tenants.value.lineSeparatorColor || '#cccccc';

    return {
      height: `${tenants.value.lineSeparatorThickness || 1}px`,
      backgroundColor:
        separatorColor && isHex8(separatorColor) ? hex8ToRgba(separatorColor) : separatorColor,
      width: '100%',
    };
  });

  const pages = computed<Business[][]>(() => {
    if (!tenants.value.businesses) return [];

    const filteredBusinesses = tenants.value.businesses.filter(
      (business: Business) => business.visible !== false,
    );

    return divideArray(filteredBusinesses, tenants.value.tenantsOnSlide);
  });

  function setCurrentPage(value: number) {
    currentPage.value = value;
  }

  function nextTenantsSlide() {
    currentPage.value = currentPage.value < pages.value.length ? currentPage.value + 1 : 1;
  }

  function destroyTimer() {
    if (!timer.value) return;

    clearInterval(timer.value);
  }

  function resetSlide() {
    destroyTimer();
    setCurrentPage(1);

    if (pages.value.length <= 1) {
      return;
    }

    if (initialLoad.value) {
      initialLoad.value = false;
      setTimeout(() => {
        skipInitialAnimation.value = false;

        nextTenantsSlide();

        timer.value = setInterval(nextTenantsSlide, slideInterval.value * 1000);
      }, slideInterval.value * 1000);
    } else {
      skipInitialAnimation.value = false;
      timer.value = setInterval(nextTenantsSlide, slideInterval.value * 1000);
    }
  }

  async function fetchTenants() {
    if (tenants.value.address.id === 'demo') return;

    errorLoading.value = false;
    isLoading.value = true;

    try {
      const response = await apiGetDetailedCommercialAddress(tenants.value.address.id);
      const { tenants: businesses } = response;

      if (!businesses) return;

      tenants.value.businesses = tenants.value.businesses.map((business: CommercialTenant) => {
        const currentBusiness = businesses.find(
          (current: CommercialTenant) => current.externalId === business.externalId,
        );

        const hideTenant = currentBusiness && !currentBusiness.name;
        const name = currentBusiness ? currentBusiness.name : business.name;

        return Object.assign({}, business, {
          visible: hideTenant || (business && business.visible === false) ? false : true,
          displayName: business.displayName || name,
        });
      });

      fetchedTenants.value = tenants.value.businesses;
    } catch (error) {
      logger.addLog('FetchTenants - error: ' + error);
      errorLoading.value = true;
    } finally {
      isLoading.value = false;
    }
  }

  onMounted(() => {
    resetSlide();
    fetchTenants();
  });

  onUnmounted(() => {
    destroyTimer();
  });
</script>

<template>
  <div v-if="isLoading && (!fetchedTenants || !fetchedTenants.length)" class="loader-container">
    <Loader :size="30" title="Loading Tenants..." />
  </div>

  <div v-else-if="errorLoading" class="error-message">Data not available</div>

  <div v-else-if="fetchedTenants" class="tenants-wrapper" :style="wrapperStyle">
    <div v-if="tenants.showAddress" class="address-title" :style="addressTitleStyle">
      {{ tenants.addressText || tenants.address.name || 'Hyresgäster' }}
    </div>

    <div class="tenants-container">
      <div
        v-for="(page, idx) in pages"
        :class="{
          slideIn: idx === currentPage - 1 && !skipInitialAnimation,
          slideOut: idx !== currentPage - 1 && !skipInitialAnimation,
          'initial-slide': idx === currentPage - 1 && skipInitialAnimation,
          'hidden-slide': idx !== currentPage - 1 && skipInitialAnimation,
        }"
        :key="idx"
        class="tenants-page"
        :style="containerStyle"
      >
        <div class="businesses">
          <template v-for="(business, index) in page">
            <div
              class="tenants"
              :style="businessesContainerStyle"
              :key="`${business.floor}-${business.externalId}`"
            >
              <div class="tenant-name" :style="tenantNameStyle">
                {{ business.displayName || business.name }}
              </div>
              <div class="tenant-number" :style="tenantNumberStyle">{{ business.floor }}</div>
            </div>
            <div
              v-if="tenants.showLineSeparator && index < page.length - 1"
              class="line-separator"
              :style="lineSeparatorStyle"
              :key="`${business.floor}-${business.externalId}-${index}`"
            ></div>
          </template>
        </div>
      </div>
    </div>

    <div class="slides scrollbar" :style="paginationStyle">
      <template v-if="!widgetData.tenants.paginationInText">
        <div
          v-for="index in pages.length"
          :key="index"
          class="page-dot"
          @click="setCurrentPage(index)"
        >
          <div
            :class="index === currentPage ? 'current-dot' : 'dot'"
            :style="{
              backgroundColor:
                index === currentPage
                  ? widgetData.tenants.paginationFontColor &&
                    isHex8(widgetData.tenants.paginationFontColor)
                    ? hex8ToRgba(widgetData.tenants.paginationFontColor)
                    : widgetData.tenants.paginationFontColor
                  : 'auto',
            }"
          ></div>
        </div>
      </template>
      <div v-else class="pagination-in-text">
        {{ `Sida ${currentPage}/${pages.length}` }}
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
  .tenants-wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    padding-bottom: 10px;
  }

  .tenants-container {
    width: 100%;
    height: 100%;
    overflow: hidden;
    position: relative;
    flex: 1;
  }

  .tenants-page {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    transform: translateX(-100%);
    -webkit-transform: translateX(-100%);

    padding-right: 20px;
    padding-left: 20px;
    padding-bottom: 0;

    .tenants {
      display: flex;
      align-items: center;
    }

    .business-title {
      width: 100%;
      font-weight: bold;
      padding: 9px 11px;
      border-radius: 8px;
    }

    .tenant-number {
      font-weight: bold;
      width: 52px;
      margin-left: 12px;
    }
  }

  .address-title {
    width: 100%;
    overflow-wrap: break-word;
  }

  .loader-container,
  .error-message {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;
  }

  .error-message {
    font-size: 16px;
    text-align: center;
  }

  .slideIn {
    animation: slide-in 1s forwards;
    -webkit-animation: slide-in 1s forwards;
  }

  .slideOut {
    animation: slide-out 1s forwards;
    -webkit-animation: slide-out 1s forwards;
  }

  .initial-slide {
    transform: translateX(0%) !important;
    -webkit-transform: translateX(0%) !important;
  }

  .hidden-slide {
    transform: translateX(-100%) !important;
    -webkit-transform: translateX(-100%) !important;
  }

  @keyframes slide-in {
    0% {
      transform: translateX(100%);
    }
    100% {
      transform: translateX(0%);
    }
  }

  @-webkit-keyframes slide-in {
    100% {
      -webkit-transform: translateX(0%);
    }
  }

  @keyframes slide-out {
    0% {
      transform: translateX(0%);
    }
    100% {
      transform: translateX(-100%);
    }
  }

  @-webkit-keyframes slide-out {
    0% {
      -webkit-transform: translateX(0%);
    }
    100% {
      -webkit-transform: translateX(-100%);
    }
  }

  .slides {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px 20px;
    width: 100%;
    flex-wrap: wrap;

    .page-dot {
      display: flex;
      align-items: center;
      justify-content: center;

      width: 34px;
      height: 20px;
      border-radius: 100%;
      padding: 3px 10px;

      &:hover {
        cursor: pointer;

        .dot {
          background-color: #151515;
          opacity: 40%;
        }
      }
    }

    .dot,
    .current-dot {
      background-color: #151515;
      border-radius: 100%;
    }

    .dot {
      opacity: 20%;
      width: 8px;
      height: 8px;
    }

    .current-dot {
      background-color: #151515;
      width: 14px;
      height: 14px;
    }
  }

  .line-separator {
    border: none;
    outline: none;
  }
</style>
